import { hot } from 'react-hot-loader';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import IdleTimer from 'react-idle-timer';
import { ToastContainer } from 'react-toastify';
import * as Raven from 'raven-js';
import ReactGA from 'react-ga';

import { AppSidebar, Header, UploadProgress } from 'Containers';
import { setIdleTime } from 'Common/Security/SecurityHelpers';
import {
  SESSION_IDLE_BEGINS_SECONDS,
  TOAST_HANG_TIME,
  TOAST_Z_INDEX,
} from 'Common/Constants';
import { DirtyContext } from 'Components/tracked/DirtyContext';
import { ContextMenuDisplay } from 'Components/app/contextmenu/ContextMenuDisplay';
import EventManager from 'Components/app/contextmenu/ContextMenuEventManager';
import { ContextMenuEvent } from 'Components/app/contextmenu/ContextMenuManager';
import { NotificationDialogManager } from 'Components/';
import { YesNoDialogManager } from 'Components/';
import { AccessPromptManager } from 'Common/Components/AccessPrompt/AccessPromptManager';
import { appStartUp } from 'State/app/actions';
import { setOnline } from 'State/connection/actions';
import { setFullscreen } from 'Common/UI/state/actions';
import * as ConnectionSelectors from 'State/connection/selectors';
import * as ConnectionActions from 'State/connection/actions';
import { SupportTools } from 'Common/SupportTools/SupportTools';

import { init as initPendo } from './Pendo';
import {
  env,
  ENV_PRODUCTION,
  HOT_RELOAD,
  IS_DEV,
  IS_STAGING,
  IS_IN_HOUSE_MASTER,
  IS_AZURE_DEV,
  IS_AZURE_QA,
} from '../../env';
import { Routes } from '../../routes';

import 'react-toastify/dist/ReactToastify.css';

class AppContainer extends React.Component {
  isDirty = false;

  componentDidMount() {
    this.props.appStartUp();
    //navigate away when dirty messsage
    window.addEventListener('beforeunload', this.handleBeforeunload);
    window.addEventListener('online', this.handleOnlineChange);
    window.addEventListener('offline', this.handleOnlineChange);
    window.addEventListener('fullscreenchange', this.handleFullscreenChange);

    //Add logging and analytics tools to production but not sheldon production
    if (this.isRealProduction()) {
      Raven.config(
        'https://174680152fd84c3fb023a70e1bcff88e@sentry.io/1381191',
        {}
      ).install();

      ReactGA.initialize('UA-3486855-13');
      this.props.history.listen((location) => {
        const firstBitOfUrl = '/' + location.pathname.split('/')[1];

        ReactGA.pageview(firstBitOfUrl);
      });
    }

  // get initial online and fullscreen values
    this.handleOnlineChange();
    this.handleFullscreenChange();
  }

  // Determines if this environment is production
  isRealProduction = () => {
    if (env !== ENV_PRODUCTION) {
      return false;
    }

    // Uses a blacklist to determine if it is a non production process environment. Probably should be a whitelist where if is.
    // Pendo and Google Analytics depend on this.
    if (
      process.env &&
      (process.env[IS_DEV] ||
        process.env[IS_STAGING] ||
        process.env[IS_IN_HOUSE_MASTER] ||
        process.env[IS_AZURE_DEV] ||
        process.env[IS_AZURE_QA])
    ) {
      return false;
    }

    return true;
  };

  componentWillUnmount() {
    //navigate away when dirty messsage
    window.removeEventListener('beforeunload', this.handleBeforeunload);
    window.removeEventListener('online', this.handleOnlineChange);
    window.removeEventListener('offline', this.handleOnlineChange);
  }

  componentDidCatch(error, info) {
    // Display fallback UI?
    if (this.isRealProduction()) {
      Raven.captureException(error, { extra: info });
    }
  }

  handleOnlineChange = () => {
    const { serverUp, hideOfflinePrompt } = this.props;

    const online = window.navigator.onLine;
    this.props.setOnline(online);

    // Handle case where WiFi might be disconnected but comes back up before the socket
    // ever had the chance to disconnect which leaves us in the state of the prompt being visible
    // but the server is considered up and running
    if (online && serverUp) {
      hideOfflinePrompt();
    }
  };

  getIsFullscreen = () =>
    Boolean(
      document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement
    );

  /** Update store with current fullscreen */
  handleFullscreenChange = () => {
    this.props.setFullscreen(this.getIsFullscreen());
  };

  handleBeforeunload = (event) => {
    if (this.isDirty) {
      //this string doesn't seem to be
      //used by the browser
      event.returnValue = 'You will lose your data!!';
      return event.returnValue;
    }
  };

  handleScroll = () => {
    EventManager.emit(ContextMenuEvent.CLOSE_MENU);
  };

  //needed for the message that
  //shows when a form is dirty and you navigate away
  //using the dirty calculated from the store at this level
  //causes child components to re-render and you lose component state
  //so passdown a function to allow the dirty to be felt at this level
  setPageLevelDirty = (isDirty) => {
    this.isDirty = isDirty;
  };

  getPageLevelDirty = () => this.isDirty;

  idleAction = () => {
    //store the time we went idle
    //Check in the HOC to see if we exceeded the session time
    const idleTimeUTC = moment.utc().format();
    setIdleTime(idleTimeUTC);
  };

  activeAction = () => {
    //No longer idle, clear the value
    setIdleTime(null);
  };

  scrollMainTo = (x = 0, y = 0) => {
    const mainSection = document.getElementById('main-section');

    if (mainSection) {
      mainSection.scrollTo(x, y);
    }
  };

  renderApp = (context) => {
    if (window.location.pathname === '/') {
      return <Routes {...context} />;
    } else {
      const idleMS = SESSION_IDLE_BEGINS_SECONDS * 1000;

      return (
        <React.Fragment>
          <IdleTimer
            element={document}
            onIdle={this.idleAction}
            onActive={this.activeAction}
            timeout={idleMS}
            key={'idleTimer'}
          />

          <ContextMenuDisplay />

          <div id="app-grid">
            <Header />
            <main id="app-main-container">
              <AppSidebar />
              <main id="app-main">
                <section id="main-section" onScroll={this.handleScroll}>
                  <Routes scrollMainTo={this.scrollMainTo} {...context} />
                </section>
              </main>
              <UploadProgress />
              <NotificationDialogManager />
              <YesNoDialogManager />
              <AccessPromptManager />
              <SupportTools />
            </main>
          </div>
        </React.Fragment>
      );
    }
  };

  render() {
    return (
      <DirtyContext.Provider
        value={{
          getPageLevelDirty: this.getPageLevelDirty,
          setPageLevelDirty: this.setPageLevelDirty,
          history: this.props.history,
        }}
      >
        <ToastContainer
          style={{ zIndex: TOAST_Z_INDEX }}
          autoClose={1000 * TOAST_HANG_TIME}
          position="top-center"
        />
        <DirtyContext.Consumer>
          {(context) => this.renderApp(context)}
        </DirtyContext.Consumer>
      </DirtyContext.Provider>
    );
  }
}

const mapStateToProps = (state) => ({
  serverUp: ConnectionSelectors.getServerUp(state),
});

const mapDispatchToProps = {
  appStartUp,
  setOnline,
  setFullscreen,
  initPendo,
  hideOfflinePrompt: ConnectionActions.hideOfflinePrompt,
};

const AppComponent = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AppContainer)
);

let App = AppComponent;

if (HOT_RELOAD) {
  // important that the hot is not a path that gets executed when HOT_RELOAD is not enabled
  App = hot(module)(AppComponent);
}

export { App };



// WEBPACK FOOTER //
// ./src/containers/app/App.js